import { v4 as uuidv4 } from 'uuid';
import { isValidUUID, validateId } from './validation';
import tablesMetadataService from '../services/tablesMetadataService';

/**
 * @fileoverview UUID utility functions for record management
 * 
 * This file provides utilities for generating, validating, and managing UUIDs
 * used for record synchronization between client and server.
 */

/**
 * Generates a v4 UUID for use in record identification
 * @returns {string} A new UUID v4 string
 */
export const generateUuid = () => {
  return uuidv4();
};

/**
 * Ensures a record has a valid UUID, generating one if needed
 * @param {Object} record - The record to ensure has a UUID
 * @param {string} tableName - The name of the table this record belongs to
 * @returns {Object} The record with a guaranteed UUID
 */
export const ensureRecordHasUuid = (record, tableName) => {
  if (!record) {
    throw new Error('Record cannot be null or undefined');
  }

  // Check if this table requires UUIDs for sync
  const requiresUuid = tablesMetadataService.requiresUuidForSync(tableName);
  
  // If it doesn't require a UUID, return the record as is
  if (!requiresUuid) {
    return record;
  }
  
  // If the record already has a valid UUID, return it unchanged
  if (record.uuid) {
    try {
      validateId(record.uuid);
      return record;
    } catch (error) {
      console.warn(`Record in ${tableName} has invalid UUID: ${record.uuid}. Generating a new one.`);
    }
  }
  
  // Generate and assign a new UUID
  return {
    ...record,
    uuid: generateUuid()
  };
};

/**
 * List of tables where we can safely auto-fix invalid UUIDs
 * instead of throwing errors
 */
const AUTO_FIX_TABLES = [
  'UserConfig',
  'UserActionHistory',
  'Notification'
];

/**
 * Validates that a record has a proper UUID if it's in a syncable table
 * @param {Object} record - The record to validate
 * @param {string} tableName - The name of the table this record belongs to
 * @returns {boolean} Whether the UUID is valid
 * @throws {Error} If the UUID is missing or invalid and the table requires it
 */
export const validateRecordUuid = (record, tableName) => {
  if (!record) {
    throw new Error('Record cannot be null or undefined');
  }

  // Check if this table requires UUIDs for sync
  const requiresUuid = tablesMetadataService.requiresUuidForSync(tableName);
  
  // If it doesn't require a UUID, return true
  if (!requiresUuid) {
    return true;
  }
  
  // Check if record has a UUID
  if (!record.uuid) {
    // For certain non-critical tables, auto-fix instead of throwing
    if (AUTO_FIX_TABLES.includes(tableName)) {
      console.warn(`Record for table ${tableName} is missing UUID. Auto-fixing.`);
      record.uuid = generateUuid();
      // Update lastModified to ensure it gets synced
      record.lastModified = new Date().toISOString();
      return true;
    }
    
    throw new Error(`Record for table ${tableName} is missing UUID`);
  }
  
  // Validate the UUID
  try {
    validateId(record.uuid);
    return true;
  } catch (error) {
    // For certain non-critical tables, auto-fix instead of throwing
    if (AUTO_FIX_TABLES.includes(tableName)) {
      console.warn(`Record for table ${tableName} has invalid UUID format: ${record.uuid}. Auto-fixing.`);
      record.uuid = generateUuid();
      // Update lastModified to ensure it gets synced
      record.lastModified = new Date().toISOString();
      return true;
    }
    
    throw new Error(`Record for table ${tableName} has invalid UUID format: ${record.uuid}`);
  }
};

/**
 * Updates a record's lastModified timestamp to the current time
 * @param {Object} record - The record to update
 * @returns {Object} The updated record
 */
export const updateLastModified = (record) => {
  if (!record) {
    throw new Error('Record cannot be null or undefined');
  }

  // If this is a server record, preserve its timestamp exactly as is
  if (record._fromServer === true && record.lastModified) {
    return { ...record };
  }

  return {
    ...record,
    lastModified: new Date().toISOString()
  };
};

/**
 * Prepares a record for synchronization by ensuring it has all required fields
 * @param {Object} record - The record to prepare
 * @param {string} tableName - The name of the table this record belongs to
 * @param {boolean} [updateTimestamp=true] - Whether to update the lastModified timestamp
 * @returns {Object} The prepared record
 */
export const prepareRecordForSync = (record, tableName, updateTimestamp = true) => {
  if (!record) {
    throw new Error('Record cannot be null or undefined');
  }

  let preparedRecord = { ...record };
  
  // Ensure the record has a valid UUID
  preparedRecord = ensureRecordHasUuid(preparedRecord, tableName);
  
  // Server records should preserve their timestamp exactly
  const isServerRecord = record._fromServer === true;
  
  // Only update the lastModified timestamp if requested AND not a server record
  // This allows us to preserve server timestamps when syncing
  if (updateTimestamp && !isServerRecord) {
    preparedRecord = updateLastModified(preparedRecord);
  } else if (isServerRecord && record.lastModified) {
    // Ensure we preserve the exact server timestamp
    preparedRecord.lastModified = record.lastModified;
  }
  
  // Handle any special dependencies
  preparedRecord = handleRecordDependencies(preparedRecord, tableName);
  
  return preparedRecord;
};

/**
 * Manages dependency relationships for records
 * Particularly for Invitation -> Circle relationships
 * @param {Object} record - The record to check for dependencies
 * @param {string} tableName - The name of the table this record belongs to
 * @returns {Object} The record with dependencies handled
 */
export const handleRecordDependencies = (record, tableName) => {
  if (!record) {
    throw new Error('Record cannot be null or undefined');
  }

  // Handle special case for Invitation records
  if (tableName === 'Invitation') {
    let jsonData = record.jsonData;
    
    // Parse jsonData if it's a string
    if (typeof jsonData === 'string') {
      try {
        jsonData = JSON.parse(jsonData);
      } catch (error) {
        console.error('Error parsing invitation jsonData:', error);
        // Return record unchanged if we can't parse the JSON
        return record;
      }
    }
    
    // If this invitation references a Circle that was created locally
    // (id = -1), we need to store the UUID to update the reference later
    if (jsonData && jsonData.circleId === -1 && !jsonData.CircleUuid) {
      // We need to find the Circle's UUID
      // This should ideally be done when creating the invitation
      console.warn('[uuidHelper] Invitation references locally created Circle but lacks UUID reference');
      console.log('[uuidHelper] record', record);
      console.log('[uuidHelper] jsonData', jsonData);
    }
    
    // Stringify the jsonData if it was originally a string
    if (typeof record.jsonData === 'string') {
      return {
        ...record,
        jsonData: JSON.stringify(jsonData)
      };
    } else {
      return {
        ...record,
        jsonData
      };
    }
  }
  
  // No special handling needed for other tables (yet)
  return record;
}; 